import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  Skeleton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyIcon from '@mui/icons-material/Key';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { INK, MUTED, RULE } from '../../analytics/analyticsUi';

export const STATUS_META = {
  active: { label: 'Active', color: '#1BAA52' },
  pending: { label: 'Pending', color: '#C77700' },
  suspended: { label: 'Suspended', color: '#C2410C' }
};

export const VERIFICATION_META = {
  verified: { label: 'Verified', color: '#1BAA52' },
  in_progress: { label: 'Verification in progress', color: '#0E7C86' },
  unverified: { label: 'Not verified', color: '#C77700' },
  rejected: { label: 'Verification rejected', color: '#C2410C' }
};

const statusMeta = (status) => STATUS_META[status] || { label: status || 'Unknown', color: MUTED };
const verificationMeta = (status) => VERIFICATION_META[status] || { label: status || 'Unknown', color: MUTED };

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const Status = ({ account }) => {
  const status = statusMeta(account.status);
  const verification = verificationMeta(account.verificationStatus);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{status.label}</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: verification.color === '#1BAA52' ? MUTED : verification.color, display: 'block', pl: 2 }}>
        {verification.label}
      </Typography>
      {account.isOnBizApp && (
        <Typography variant="caption" sx={{ color: MUTED, display: 'block', pl: 2 }}>
          Also in WhatsApp Business app
        </Typography>
      )}
      {account.selfPay && (
        <Typography variant="caption" sx={{ color: MUTED, display: 'block', pl: 2 }}>
          Self-pay: pays Meta for messages
        </Typography>
      )}
      {account.isBusinessApp && account.isOnBizApp === false && (
        <Typography variant="caption" sx={{ color: '#C77700', display: 'block', pl: 2 }}>
          No longer linked to the Business app
        </Typography>
      )}
    </Box>
  );
};

const CopyableId = ({ label, value, onCopy }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: MUTED, width: 64, flexShrink: 0 }}>{label}</Typography>
    {value ? (
      <>
        <Typography
          variant="body2"
          title={value}
          sx={{ color: INK, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {value}
        </Typography>
        <Tooltip title={`Copy ${label}`}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onCopy(value, label); }}
            aria-label={`Copy ${label} ${value}`}
            sx={{ p: 0.5, color: MUTED }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </>
    ) : (
      <Typography variant="body2" sx={{ color: MUTED }}>Not set</Typography>
    )}
  </Box>
);

const AccountIdentity = ({ account }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ color: INK, fontWeight: 600, lineHeight: 1.35 }} noWrap title={account.businessName}>
      {account.businessName}
    </Typography>
    <Typography variant="body2" sx={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
      {account.phoneNumber}
    </Typography>
    {account.businessDescription && (
      <Typography variant="body2" sx={{ color: MUTED, maxWidth: 320 }} noWrap title={account.businessDescription}>
        {account.businessDescription}
      </Typography>
    )}
  </Box>
);

const Owner = ({ owner }) => (
  owner ? (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: INK }} noWrap>{owner.company || owner.name}</Typography>
      {owner.company && owner.name && (
        <Typography variant="caption" sx={{ color: MUTED, display: 'block' }} noWrap>{owner.name}</Typography>
      )}
    </Box>
  ) : <Typography variant="body2" sx={{ color: MUTED }}>–</Typography>
);

const RowActions = ({ account, isRefreshing, onViewAnalytics, onRefresh, onViewToken, onEdit, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  const run = (fn) => (e) => { e.stopPropagation(); close(); fn(account); };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Button size="small" onClick={() => onViewAnalytics(account)} sx={{ textTransform: 'none', fontWeight: 600 }}>
        Analytics
      </Button>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`More actions for ${account.businessName}`}
        aria-haspopup="menu"
        disabled={isRefreshing}
      >
        {isRefreshing ? <CircularProgress size={18} /> : <MoreVertIcon fontSize="small" />}
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={run(onRefresh)}>
          <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Refresh details from Meta" />
        </MenuItem>
        <MenuItem onClick={run(onViewToken)}>
          <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Copy access token" />
        </MenuItem>
        <MenuItem onClick={run(onEdit)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={run(onDelete)} sx={{ color: '#C2410C' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: '#C2410C' }} /></ListItemIcon>
          <ListItemText primary="Delete account" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------
const AccountsList = ({
  accounts,
  loading,
  error,
  hasFilters,
  showOwner,
  page,
  rowsPerPage,
  totalAccounts,
  onPageChange,
  onRowsPerPageChange,
  isSelected,
  isAllSelected,
  isIndeterminate,
  onToggleSelect,
  onToggleSelectAll,
  refreshingAccounts,
  onCopy,
  onViewAnalytics,
  onRefresh,
  onViewToken,
  onEdit,
  onDelete,
  onConnectNew,
  onRetry,
  onClearFilters
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const actionProps = { onViewAnalytics, onRefresh, onViewToken, onEdit, onDelete };

  const frame = (children) => (
    <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>{children}</Paper>
  );

  if (loading) {
    return frame(
      <Box sx={{ p: { xs: 2, sm: 3 } }} aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 3, py: 1.75, borderTop: i ? `1px solid ${RULE}` : 'none' }}>
            <Box sx={{ flex: 2 }}><Skeleton width="60%" /><Skeleton width="35%" /></Box>
            <Box sx={{ flex: 2, display: { xs: 'none', md: 'block' } }}><Skeleton width="80%" /><Skeleton width="70%" /></Box>
            <Box sx={{ flex: 1 }}><Skeleton width="50%" /></Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return frame(
      <Box sx={{ p: { xs: 3, sm: 5 } }}>
        <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Accounts could not be loaded</Typography>
        <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>{error}</Typography>
        <Button variant="outlined" onClick={onRetry} sx={{ textTransform: 'none' }}>Try again</Button>
      </Box>
    );
  }

  if (!accounts.length) {
    return frame(
      <Box sx={{ p: { xs: 3, sm: 5 } }}>
        {hasFilters ? (
          <>
            <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No accounts match these filters</Typography>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>Try a different search, or clear the filters to see every account.</Typography>
            <Button variant="outlined" onClick={onClearFilters} sx={{ textTransform: 'none' }}>Clear filters</Button>
          </>
        ) : (
          <>
            <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No WhatsApp accounts yet</Typography>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>
              Connect a WhatsApp Business account to send messages through the API and see delivery, category and cost analytics.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onConnectNew} sx={{ textTransform: 'none' }}>
              Connect WhatsApp Business
            </Button>
          </>
        )}
      </Box>
    );
  }

  const pagination = (
    <TablePagination
      component="div"
      count={totalAccounts}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={[10, 25, 50]}
      labelRowsPerPage="Per page"
      sx={{ borderTop: `1px solid ${RULE}`, color: MUTED }}
    />
  );

  // Mobile: stacked rows
  if (isMobile) {
    return frame(
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: `1px solid ${RULE}` }}>
          <Checkbox
            size="small"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={onToggleSelectAll}
            inputProps={{ 'aria-label': 'Select all accounts on this page' }}
          />
          <Typography variant="body2" sx={{ color: MUTED }}>Select all</Typography>
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {accounts.map((account, index) => (
            <Box
              component="li"
              key={account._id}
              sx={{
                px: 1,
                py: 1.5,
                borderTop: index ? `1px solid ${RULE}` : 'none',
                bgcolor: isSelected(account._id) ? 'rgba(37, 211, 102, 0.06)' : 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Checkbox
                  size="small"
                  checked={isSelected(account._id)}
                  onChange={() => onToggleSelect(account._id)}
                  inputProps={{ 'aria-label': `Select ${account.businessName}` }}
                />
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.75 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <AccountIdentity account={account} />
                    <Status account={account} />
                  </Box>
                  {showOwner && account.user && (
                    <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                      Customer: {account.user.company || account.user.name}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1.25, display: 'grid', gap: 0.25 }}>
                    <CopyableId label="WABA ID" value={account.wabaId} onCopy={onCopy} />
                    <CopyableId label="Phone ID" value={account.phoneNumberId} onCopy={onCopy} />
                  </Box>
                  <Box sx={{ mt: 1, ml: -1 }}>
                    <RowActions account={account} isRefreshing={!!refreshingAccounts[account._id]} {...actionProps} />
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        {pagination}
      </>
    );
  }

  // Desktop: table
  return frame(
    <>
      <TableContainer>
        <Table sx={{ minWidth: showOwner ? 980 : 860 }}>
          <TableHead>
            <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, py: 1.25 } }}>
              <TableCell padding="checkbox" sx={{ pl: 1 }}>
                <Checkbox
                  size="small"
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={onToggleSelectAll}
                  inputProps={{ 'aria-label': 'Select all accounts on this page' }}
                />
              </TableCell>
              <TableCell>Account</TableCell>
              {showOwner && <TableCell>Customer</TableCell>}
              <TableCell>Meta IDs</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ pr: 2 }}><Box component="span" sx={visuallyHidden}>Actions</Box></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((account) => {
              const selected = isSelected(account._id);
              return (
                <TableRow
                  key={account._id}
                  hover
                  selected={selected}
                  onClick={() => onToggleSelect(account._id)}
                  sx={{
                    cursor: 'pointer',
                    '& td': { borderColor: RULE, py: 1.5, verticalAlign: 'top' },
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'rgba(37, 211, 102, 0.06)' }
                  }}
                >
                  <TableCell padding="checkbox" sx={{ pl: 1, pt: '14px !important' }}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => onToggleSelect(account._id)}
                      inputProps={{ 'aria-label': `Select ${account.businessName}` }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <AccountIdentity account={account} />
                  </TableCell>
                  {showOwner && (
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Owner owner={account.user} />
                    </TableCell>
                  )}
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Box sx={{ display: 'grid', gap: 0.25 }}>
                      <CopyableId label="WABA ID" value={account.wabaId} onCopy={onCopy} />
                      <CopyableId label="Phone ID" value={account.phoneNumberId} onCopy={onCopy} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Status account={account} />
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2 }}>
                    <RowActions account={account} isRefreshing={!!refreshingAccounts[account._id]} {...actionProps} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </>
  );
};

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1
};

AccountsList.propTypes = {
  accounts: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  hasFilters: PropTypes.bool,
  showOwner: PropTypes.bool,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalAccounts: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  isSelected: PropTypes.func.isRequired,
  isAllSelected: PropTypes.bool,
  isIndeterminate: PropTypes.bool,
  onToggleSelect: PropTypes.func.isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  refreshingAccounts: PropTypes.object,
  onCopy: PropTypes.func.isRequired,
  onViewAnalytics: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onViewToken: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onConnectNew: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired
};

export default AccountsList;
